// useMediaQuery — subscribe to a CSS media query in JS, for the rare behaviour
// that can't be pure CSS. The entity peek is the first consumer: its modality
// follows width (ADR-036 §2) — a non-modal side panel ≥ md, a modal SlideOver
// below — and modal-vs-non-modal is a behavioural fork (focus trap, scrim), not
// a style toggle, so a class can't express it.
//
// SSR/first-render safe: returns `false` when there's no `window` (or no
// matchMedia), then corrects on mount. Re-evaluates on viewport change so the
// Storybook viewport toolbar drives it live.

import { useEffect, useState } from 'react';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange(); // sync in case the query changed between render and effect
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
